import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Group } from '@visx/group';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { scaleLinear, scaleSqrt } from '@visx/scale';
import { Grid } from '@visx/grid';
import { useTooltip } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import BubbleImage from '../../components/ChartsImages/BubbleChartImage';
import ChartTooltip from '../../components/ChartTooltip';
import { TooltipData } from '../../components/ChartTooltip/ChartTooltip';
import uniqid from 'uniqid';
import {
  getColors,
  MAP_CHART_FIELDS_LABEL,
  METRICS,
  FormatUtils,
  useWindowSize,
  getPopulationPerc
} from '../../utils';
import styled from 'styled-components';
import { BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';

const StyledBubbleChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #ffffff;
  padding: 10px;
`;

type BubbleChartProps = {
  margin: any;
  data: any[];
  axesProps: any;
  color: string;
  emptyDataMessage: string;
  limit: any;
  showItemsPreview: boolean;
};

let tooltipTimeout: number;
const defaultMargin = { top: 50, right: 50, bottom: 50, left: 100 };

const BubbleChart: FC<BubbleChartProps> = ({
  data,
  margin = defaultMargin,
  axesProps = {},
  color,
  emptyDataMessage,
  limit = 'auto',
  showItemsPreview
}) => {
  const isEmpty = !data || data.length === 0;
  const { xAxis, yAxis, zAxis, xLabel, yLabel } = axesProps;

  const svgRef = useRef(null);
  const chartId = useRef(uniqid());
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip
  } = useTooltip<TooltipData>();

  const windowSize = useWindowSize();

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );

  const filteredData = useMemo(() => {
    if (isEmpty) {
      return [];
    }
    return limit !== 'auto' ? data.slice(0, limit) : data;
  }, [isEmpty, data, limit]);

  const [xValues, yValues, rValues] = (filteredData &&
    filteredData.reduce(
      ([xList, yList, rList]: any, item: any) => {
        xList.push(item[xAxis]);
        yList.push(item[yAxis]);
        rList.push(zAxis === METRICS.BASE ? item[zAxis] * 100 : item[zAxis]);
        return [xList, yList, rList];
      },
      [[], [], []]
    )) || [[], [], []];

  const minMaxValues = useMemo(
    () => ({
      xMin: Math.min(...xValues),
      xMax: Math.max(...xValues),
      yMin: Math.min(...yValues),
      yMax: Math.max(...yValues),
      rMin: Math.min(...rValues),
      rMax: Math.max(...rValues)
    }),
    [xValues, yValues, rValues]
  );

  const xDiff = Math.max(minMaxValues.xMax - minMaxValues.xMin, 0);
  const yDiff = Math.max(minMaxValues.yMax - minMaxValues.yMin, 0);

  const yDomain = useMemo(
    () => [minMaxValues.yMin - yDiff / 3, minMaxValues.yMax + yDiff / 3],
    [minMaxValues, yDiff]
  );

  const xDomain = useMemo(
    () => [
      minMaxValues.xMin - xDiff / 10 < 0 ? 0 : minMaxValues.xMin - xDiff / 10,
      minMaxValues.xMax + xDiff / 10
    ],
    [minMaxValues, xDiff]
  );

  const xScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: xDomain,
        range: [0, minMaxValues.xMax],
        nice: true
      }),
    [minMaxValues, xDomain]
  );

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: yDomain,
        range: [minMaxValues.yMax, 0],
        nice: true
      }),
    [minMaxValues, yDomain]
  );

  const rScale = useMemo(
    () =>
      scaleSqrt<number>({
        domain: [minMaxValues.rMin, minMaxValues.rMax],
        range: [10, 50],
        nice: true
      }),
    [minMaxValues]
  );

  const colorScale = useMemo(
    () =>
      scaleLinear<string>({
        domain: [
          0,
          isEmpty ? 0 : filteredData.length / 2,
          isEmpty ? 0 : filteredData.length
        ],
        range: getColors(color)
      }),
    [isEmpty, filteredData, color]
  );

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  xScale.rangeRound([0, xMax]);
  yScale.rangeRound([yMax, 0]);

  const yTicks = Math.min(
    yAxis === METRICS.AFFINITY ? yDiff : yDiff <= 0.01 ? 1 : yDiff * 100,
    10
  );

  const xTicks = useMemo(() => {
    let min = xDiff < 1 ? 5 : xDiff + 4;
    if (xAxis !== METRICS.AFFINITY) {
      min = xDiff <= 0.01 ? 1 : xDiff * 100;
    }

    if (width < 520) {
      return Math.min(min, 2);
    }
    return Math.min(min, 10);
  }, [xDiff, width]);

  const handleMouseLeave = useCallback(
    (event) => {
      tooltipTimeout = window.setTimeout(() => {
        hideTooltip();
      }, 0);
    },
    [hideTooltip]
  );

  const handleMouseMove = useCallback(
    (event, item, indx) => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      const eventSvgCoords = localPoint(event);
      showTooltip({
        tooltipData: {
          ...item,
          color: item.color || colorScale(indx)
        },
        tooltipTop: eventSvgCoords?.y,
        tooltipLeft: eventSvgCoords?.x
      });
    },
    [showTooltip, colorScale]
  );

  if (isEmpty) {
    return (
      <StyledBubbleChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledBubbleChartWrapper>
    );
  }

  return (
    <StyledBubbleChartWrapper ref={chartRef}>
      <svg ref={svgRef} width={width} height={height}>
        <rect width={width} height={height} fill='#ffffff' rx={14} />
        <Grid
          top={margin.top}
          left={margin.left}
          xScale={xScale}
          yScale={yScale}
          width={xMax}
          height={yMax}
          numTicksRows={yTicks}
        />
        <Group
          top={margin.top}
          left={margin.left}
          key={`group-${chartId.current}`}
        >
          {filteredData.map((item: any, indx: number) => {
            const radius = rScale(
              zAxis === METRICS.BASE ? item[zAxis] * 100 : item[zAxis]
            );
            const bubbleX = xScale(item[xAxis]);
            const bubbleY = yScale(item[yAxis]);

            if (bubbleX && bubbleY) {
              return (
                <BubbleImage
                  key={`bubble-${chartId.current}-${item.id}`}
                  item={item}
                  x={bubbleX}
                  y={bubbleY}
                  radius={radius}
                  color={item.color || colorScale(indx)}
                  onMouseMove={(event: any) =>
                    handleMouseMove(event, item, indx)
                  }
                  onMouseLeave={(event: any) => handleMouseLeave(event)}
                  chartId={chartId.current}
                  showItemsPreview={showItemsPreview}
                />
              );
            }
            return null;
          })}
          <AxisLeft
            label={yLabel}
            scale={yScale}
            stroke='#9493A6'
            tickStroke='#9493A6'
            tickFormat={(d: any) => FormatUtils.formattedValue(d, yAxis)}
            tickLabelProps={() => ({
              fill: '#9493A6',
              fontSize: 11,
              textAnchor: 'end',
              dy: '0.33em',
              dx: '-0.33em',
              fontWeight: 'bold'
            })}
            labelProps={{
              fill: 'black',
              textAnchor: 'middle',
              fontSize: 14,
              fontWeight: 'bold',
              dx: '-1.2em'
            }}
            numTicks={yTicks}
          />
          <AxisBottom
            label={xLabel}
            top={yMax}
            scale={xScale}
            numTicks={xTicks}
            stroke='#9493A6'
            tickStroke='#9493A6'
            tickFormat={(d: any) => FormatUtils.formattedValue(d, xAxis)}
            tickLabelProps={() => ({
              fill: '#9493A6',
              fontSize: 11,
              textAnchor: 'middle',
              fontWeight: 'bold'
            })}
            labelProps={{
              fill: 'black',
              textAnchor: 'middle',
              fontSize: 14,
              fontWeight: 'bold'
            }}
          />
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <ChartTooltip
          opened={tooltipOpen}
          top={tooltipTop || 0}
          left={tooltipLeft || 0}
          data={tooltipData}
        >
          <div>
            <span>Reach: </span>
            {FormatUtils.formattedValue(
              getPopulationPerc(tooltipData),
              METRICS.REACH
            )}
          </div>
          <div>
            <span>Affinity: </span>
            {FormatUtils.formattedValue(
              tooltipData.target_audience_affinity,
              METRICS.AFFINITY
            )}
          </div>
          <div>
            <span>Base: </span>
            {FormatUtils.formattedValue(
              tooltipData.base_population_perc,
              METRICS.BASE
            )}
          </div>
          <div>
            <span>Penetration: </span>
            {FormatUtils.formattedValue(
              tooltipData.penetration,
              METRICS.PENETRATION
            )}
          </div>
        </ChartTooltip>
      )}
    </StyledBubbleChartWrapper>
  );
};

export default BubbleChart;
